import React from 'react'
import { Eye, Target, BarChart3, TrendingUp, TrendingDown, MapPin } from 'lucide-react'

const MetricsReference: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Metrics Reference</h1>
        <p className="mt-2 text-sm text-gray-600">
          Understanding the key performance metrics used across the platform.
        </p>
      </div>

      {/* SoLV */}
      <section className="mb-8 bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Eye className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">SoLV (Share of Local Voice)</h2>
            <p className="text-sm text-gray-500">Primary visibility metric</p>
          </div>
        </div>
        <p className="text-sm text-gray-700 mb-3">
          The percentage of geographic search points where your business ranks in the{' '}
          <strong>top 3 positions</strong> (the Google Local 3-Pack). This is the industry-standard
          measure of local search dominance.
        </p>
        <div className="bg-gray-50 rounded-lg p-4 mb-3">
          <p className="text-sm font-mono text-gray-800">
            SoLV = (Grid points in top 3) / (Total grid points) &times; 100
          </p>
        </div>
        <div className="text-sm text-gray-600 space-y-1">
          <p>
            <strong>Example:</strong> If your business is searched from 50 geographic points and
            ranks in the top 3 at 30 of them, your SoLV is 60%.
          </p>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 text-center text-sm">
          <div className="bg-green-50 rounded-lg p-3">
            <div className="font-semibold text-green-700">60%+</div>
            <div className="text-green-600">Dominant</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3">
            <div className="font-semibold text-yellow-700">30-59%</div>
            <div className="text-yellow-600">Competitive</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3">
            <div className="font-semibold text-red-700">&lt;30%</div>
            <div className="text-red-600">Needs improvement</div>
          </div>
        </div>
      </section>

      {/* ATRP */}
      <section className="mb-8 bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-50 rounded-lg">
            <Target className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              ATRP (Average Total Rank Position)
            </h2>
            <p className="text-sm text-gray-500">Overall ranking strength</p>
          </div>
        </div>
        <p className="text-sm text-gray-700 mb-3">
          The average ranking position across <strong>all</strong> geographic search points. Points
          where your business is not found are counted as position 21. Lower is better.
        </p>
        <div className="bg-gray-50 rounded-lg p-4 mb-3">
          <p className="text-sm font-mono text-gray-800">
            ATRP = Sum of all positions (21 for not found) / Total grid points
          </p>
        </div>
        <div className="text-sm text-gray-600 space-y-1">
          <p>
            <strong>Example:</strong> 3 points at #1, #5, and not-found (21) gives ATRP = (1 + 5 +
            21) / 3 = 9.0
          </p>
          <p>
            <strong>Key difference from ARP:</strong> ATRP includes all points in the calculation,
            even where your business doesn't appear. This prevents a misleadingly good average when
            you only rank in a small area.
          </p>
        </div>
      </section>

      {/* Per-Keyword SoLV */}
      <section className="mb-8 bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <BarChart3 className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Per-Keyword Visibility (SoLV)</h2>
            <p className="text-sm text-gray-500">Keyword-level performance</p>
          </div>
        </div>
        <p className="text-sm text-gray-700 mb-3">
          Each keyword has its own SoLV score calculated from that keyword's geographic search
          points. This shows which keywords are driving your visibility and which need attention.
        </p>
        <div className="text-sm text-gray-600">
          <p>
            <strong>Example:</strong> "personal training" may have 5% SoLV (mostly not ranking),
            while "small group training" has 40% SoLV (ranking well in several areas).
          </p>
        </div>
      </section>

      {/* Position Color Scale */}
      <section className="mb-8 bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-50 rounded-lg">
            <MapPin className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Heatmap Color Scale</h2>
            <p className="text-sm text-gray-500">Geographic ranking visualization</p>
          </div>
        </div>
        <p className="text-sm text-gray-700 mb-4">
          The heatmap uses a 5-tier color gradient to visualize your ranking position at each
          geographic search point.
        </p>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style={{ background: '#4ade80' }}
            >
              1-3
            </div>
            <div>
              <span className="font-medium text-gray-900">Green (Positions 1-3)</span>
              <span className="text-gray-500 ml-2">In the Local 3-Pack. Maximum visibility.</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style={{ background: '#a3e635' }}
            >
              4-10
            </div>
            <div>
              <span className="font-medium text-gray-900">Lime (Positions 4-10)</span>
              <span className="text-gray-500 ml-2">
                Visible but below the pack. Room to improve.
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style={{ background: '#facc15' }}
            >
              11-15
            </div>
            <div>
              <span className="font-medium text-gray-900">Yellow (Positions 11-15)</span>
              <span className="text-gray-500 ml-2">
                Low visibility. Requires scrolling to find.
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style={{ background: '#fb923c' }}
            >
              16-20
            </div>
            <div>
              <span className="font-medium text-gray-900">Orange (Positions 16-20)</span>
              <span className="text-gray-500 ml-2">
                Very low visibility. Near the bottom of results.
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style={{ background: '#f87171' }}
            >
              20+
            </div>
            <div>
              <span className="font-medium text-gray-900">Red (Position 20+ / Not Found)</span>
              <span className="text-gray-500 ml-2">
                Not visible in search results for this area.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Trend Indicators */}
      <section className="mb-8 bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-emerald-50 rounded-lg">
            <TrendingUp className="h-6 w-6 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Trend Indicators</h2>
            <p className="text-sm text-gray-500">Period-over-period changes</p>
          </div>
        </div>
        <p className="text-sm text-gray-700 mb-4">
          Trends compare the keyword's current Position value to its Position in the previous
          collection period, using the same ranking shown in the table. A value of{' '}
          <strong>20+</strong> means the business wasn't found in the top 20, and{' '}
          <strong>No Data</strong> means no ranking was available for that period.
        </p>

        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Direction
          </p>
          <p className="text-xs text-gray-500 mb-2">
            Rules are evaluated top-to-bottom; the first match wins.
          </p>
          <p className="text-sm font-mono text-gray-800 leading-relaxed">
            if either period has No Data &rarr; <span className="text-gray-700">Stable</span>
            <br />
            if both periods are 20+ &rarr; <span className="text-gray-500">Not Ranked</span>
            <br />
            if current is 20+ (previous was ranked) &rarr;{' '}
            <span className="text-red-700">Down</span> (fell out of top 20)
            <br />
            if previous was 20+ (current is ranked) &rarr;{' '}
            <span className="text-green-700">Up</span> (entered top 20)
            <br />
            if both ranked (1&ndash;20) and current &lt; previous &rarr;{' '}
            <span className="text-green-700">Up</span> (improved)
            <br />
            if both ranked (1&ndash;20) and current &gt; previous &rarr;{' '}
            <span className="text-red-700">Down</span> (declined)
            <br />
            if both ranked (1&ndash;20) and current = previous &rarr;{' '}
            <span className="text-gray-700">Stable</span>
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Magnitude (positions gained)
          </p>
          <p className="text-sm font-mono text-gray-800 leading-relaxed">
            change = previous &minus; current
          </p>
          <p className="text-xs text-gray-600 mt-2">
            Positive = moved closer to #1 (improvement). Negative = moved further from #1 (decline).
            Entering the top 20 from 20+ counts as +1; falling out of the top 20 counts as &minus;1.
          </p>
        </div>

        <div className="text-sm text-gray-600 mb-4">
          <p>
            <strong>Example:</strong> Previous position #8 &rarr; current position #3 gives change =
            8 &minus; 3 = <span className="text-green-700 font-semibold">+5</span> (Up, improved 5
            positions).
          </p>
        </div>

        <div className="space-y-3 text-sm border-t border-gray-200 pt-4">
          <div className="flex items-center gap-3">
            <span className="text-green-600 font-medium flex items-center">
              <TrendingUp size={16} className="mr-1" /> Up
            </span>
            <span className="text-gray-600">Position improved (moved closer to #1)</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-red-600 font-medium flex items-center">
              <TrendingDown size={16} className="mr-1" /> Down
            </span>
            <span className="text-gray-600">Position declined (moved further from #1)</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-500 font-medium">— Stable</span>
            <span className="text-gray-600">No change in position between periods</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 font-medium text-xs">Not Ranked</span>
            <span className="text-gray-600">Business was not found (20+) in both periods</span>
          </div>
        </div>
      </section>
    </div>
  )
}

export default MetricsReference
